/**
 * Handlers - Sets up event handlers for voice interface
 * 
 * Required from parent (voiceInterface):
 * - shadowRoot: ShadowRoot | Document
 * - isMobile: boolean
 * - startVoiceCall(): Promise<void>
 * - endVoiceCall(): void
 * - toggleMute(): void
 * - toggleSpeaker(): void
 * - sendDesktopTextMessage(): void
 * - toggleConversationPanel(): void
 * - textHandlers: TextHandlers instance
 * 
 * State owned by this module:
 * - None (only sets up event listeners)
 */

export class Handlers {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
    this.shadowRoot = voiceInterface.shadowRoot;
  }

  /**
   * Setup event handlers for voice interface
   */
  setupEventHandlers(callbacks) {
    const startCallBtn = this.shadowRoot.getElementById('startCallBtn');
    const endCallBtn = this.shadowRoot.getElementById('desktopEndCallBtn');
    const muteBtn = this.shadowRoot.getElementById('desktopMuteBtn');
    const speakerBtn = this.shadowRoot.getElementById('desktopSpeakerBtn');
    
    // Compact layout buttons (when history is expanded)
    const endCallBtnCompact = this.shadowRoot.getElementById('desktopEndCallBtnCompact');
    const muteBtnCompact = this.shadowRoot.getElementById('desktopMuteBtnCompact');
    const speakerBtnCompact = this.shadowRoot.getElementById('desktopSpeakerBtnCompact');
    
    if (startCallBtn) {
      startCallBtn.onclick = () => this.voiceInterface.startVoiceCall();
    }
    if (endCallBtn) {
      endCallBtn.onclick = () => this.voiceInterface.endVoiceCall();
    }
    if (muteBtn) {
      muteBtn.onclick = () => this.voiceInterface.toggleMute();
    }
    if (speakerBtn) {
      speakerBtn.onclick = () => this.voiceInterface.toggleSpeaker();
    }
    
    // Compact layout button handlers (same functionality)
    if (endCallBtnCompact) {
      endCallBtnCompact.onclick = () => this.voiceInterface.endVoiceCall();
    }
    if (muteBtnCompact) {
      muteBtnCompact.onclick = () => this.voiceInterface.toggleMute();
    }
    if (speakerBtnCompact) {
      speakerBtnCompact.onclick = () => this.voiceInterface.toggleSpeaker();
    }
    
    // Desktop text input handlers (only on desktop)
    if (!this.voiceInterface.isMobile) {
      const voiceTextInput = this.shadowRoot.getElementById('voiceTextInput');
      const voiceSendBtn = this.shadowRoot.getElementById('voiceSendBtn');
      
      if (voiceTextInput) {
        // Enable/disable send button based on input content
        voiceTextInput.addEventListener('input', () => {
          if (voiceSendBtn) {
            voiceSendBtn.disabled = !voiceTextInput.value.trim();
          }
        });
        
        // Handle Enter key
        voiceTextInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter' && !e.shiftKey && voiceTextInput.value.trim()) {
            e.preventDefault();
            if (voiceSendBtn && !voiceSendBtn.disabled) {
              voiceSendBtn.click();
            }
          }
        });
      }
      
      if (voiceSendBtn) {
        voiceSendBtn.onclick = () => {
          this.voiceInterface.sendDesktopTextMessage();
        };
      }
      
      // Conversation panel toggle
      const conversationHeader = this.shadowRoot.getElementById('conversationHeader');
      if (conversationHeader) {
        conversationHeader.addEventListener('click', () => {
          this.voiceInterface.transcript.toggleConversationPanel();
        });
      }
    }
  }

  /**
   * Setup mobile-specific event handlers
   */
  setupMobileEventHandlers() {
    if (!this.voiceInterface.isMobile) return;

    // Mobile bar controls
    const mobileMuteBtn = document.getElementById('mobileMuteBtn');
    const mobileSpeakerBtn = document.getElementById('mobileSpeakerBtn');
    const mobileEndCallBtn = document.getElementById('mobileEndCallBtn');
    const mobileTranscriptRow = document.getElementById('mobileTranscriptRow');
    const mobileCloseTranscriptBtn = document.getElementById('mobileCloseTranscriptBtn');
    const mobileTextInput = document.getElementById('mobileTextInput');
    const mobileSendBtn = document.getElementById('mobileSendBtn');

    if (mobileMuteBtn) {
      mobileMuteBtn.onclick = () => this.voiceInterface.toggleMute();
    }

    if (mobileSpeakerBtn) {
      mobileSpeakerBtn.onclick = () => this.voiceInterface.toggleSpeaker();
    }

    if (mobileEndCallBtn) {
      mobileEndCallBtn.onclick = () => this.voiceInterface.endVoiceCall();
    }

    // Expand transcript overlay
    if (mobileTranscriptRow) {
      mobileTranscriptRow.onclick = () => {
        this.voiceInterface.expandMobileTranscript();
      };
    }

    // Close transcript overlay
    if (mobileCloseTranscriptBtn) {
      mobileCloseTranscriptBtn.onclick = () => {
        this.voiceInterface.collapseMobileTranscript();
      };
    }

    // Mobile text input handlers
    if (mobileTextInput) {
      mobileTextInput.addEventListener('input', () => {
        if (mobileSendBtn) {
          mobileSendBtn.disabled = !mobileTextInput.value.trim();
        }
      });

      mobileTextInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey && mobileTextInput.value.trim()) {
          e.preventDefault();
          if (mobileSendBtn && !mobileSendBtn.disabled) {
            mobileSendBtn.click();
          }
        }
      });
    }

    if (mobileSendBtn) {
      mobileSendBtn.onclick = () => {
        this.voiceInterface.sendMobileTextMessage();
      };
    }
  }
}
